import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAccount } from 'wagmi'
import { T } from '@/lib/theme'
import { SectionHeader } from '@/components/dashboard/SectionHeader'
import { Badge, FilterPill, HelpIcon, Input, Panel, SectionLabel, Skeleton, Stat, Tooltip, clickable } from '@/components/ui'
import { fmt, fmtUsd, fmtPct } from '@/lib/utils'
import { useVaultsList } from '@/hooks/useVaultsList'
import { useVaultNavHistory } from '@/hooks/useVaultNavHistory'
import { useVaultPendingFees } from '@/hooks/useVaultPendingFees'
import { useVaultEmissionRates } from '@/hooks/useVaultEmissionRates'
import { useHorizonPriceUsd } from '@/hooks/useHorizonPriceUsd'
import { dilutedRewardAprBps } from '@/lib/loops'
import { EST_REFERENCE_USD } from '@/lib/coldStart'
import { TAG_LABELS, orderedPairSymbols } from '@/lib/tokenTags'
import { ADDRESSES } from '@/lib/contracts'
import { PairGlyph } from '@/components/dashboard/TokenGlyph'
import { StakingVaultsPanel } from '@/components/dashboard/StakingVaultsPanel'
import { headerRow, headCell, row } from '@/components/dashboard/tableChrome'

// ─────────────────────────────────────────────────────────────────
// Beefy-style vault listing: Spotlight strip, chain/venue/tag filters, sortable tables.
// Reads VaultFactory.activeVaults() live (see useVaultsList's own doc comment) - genesis-curated
// creation means this list only ever grows one vault at a time, so no pagination yet.
//
// APY is a real trailing rate derived from scripts/vaultNavIndexer.cjs's own NAV-per-share
// snapshot log (see useVaultNavHistory.js), and renders "—" (never a fabricated number) until that
// log has enough history.
//
// The protocol-wide stats bar (Vaults / TVL / Weekly Yield / Revenue / Buyback) was removed: those
// are protocol figures rather than anything about choosing a vault, and /stats already carries
// them. useProtocolWeeklyStats went with it - an unused hook still fires its request on every load.
// ─────────────────────────────────────────────────────────────────

const VENUE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'v3', label: 'Uniswap V3' },
  { id: 'v4', label: 'Uniswap V4' },
]

const SORTABLE = { name: (v) => v.name || '', tvl: (v) => v.tvlUsd || 0 }

export function VaultsSection() {
  const { vaults, isLoading, notDeployed } = useVaultsList()
  const { apyFor, weeklyFeesUsdFor, weeklyVolumeUsdFor } = useVaultNavHistory()
  const { pendingFeesUsdFor } = useVaultPendingFees(vaults)
  const { horizonPerDayFor } = useVaultEmissionRates()
  const { rate: horizonUsd, found: hasHorizonPrice } = useHorizonPriceUsd()
  const { address: user } = useAccount()

  // HORIZON emission APR, as a percent. A forward estimate: the vault's emission budget annualized
  // against its TVL, with a $10k reference floor (EST_REFERENCE_USD) so a near-empty vault does not
  // print an absurd rate. Vault farms are ungoverned (no APR cap until a vault-share oracle exists),
  // so the budget is the whole emission story. Computed ALWAYS - unlike the old cold-start estimate,
  // which stood in only when fee history was missing - so it can be ADDED to the realized fee APY
  // for a combined headline. `null` when the vault is not in the emissions table at all (unknown);
  // a real `0` when it is registered at zero weight.
  const emissionApyPctFor = (v) => {
    if (!hasHorizonPrice) return null
    const perDay = horizonPerDayFor(v.address)
    if (perDay == null) return null
    if (!(perDay > 0)) return 0
    return dilutedRewardAprBps(perDay * 365 * horizonUsd, v.tvlUsd || 0, EST_REFERENCE_USD) / 100
  }

  // The headline APY is the SUM of the realized trailing fee APY (from the NAV indexer) and the
  // emission estimate; the hover tooltip discloses the split so a user sees what the pool earns today
  // AND what emissions add on top - the "keep your fees, earn our emissions" wedge, made visible even
  // after real fees start flowing. Each leg is null when genuinely unknown; `combined` is null only
  // when BOTH are, preserving the honest "—" the column always kept.
  const apyInfoFor = (v) => {
    const feePct = apyFor(v.address)          // realized 7-day fee APY (%), or null (no history yet)
    const emissionPct = emissionApyPctFor(v)  // estimated emission APR (%), or null (not a farm)
    const hasFee = feePct != null
    const hasEmission = emissionPct != null
    const combined = hasFee || hasEmission ? (feePct ?? 0) + (emissionPct ?? 0) : null
    return { combined, feePct, emissionPct, hasFee, hasEmission }
  }

  const [venue, setVenue] = useState('all')
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState('tvl') // 'tvl' | 'name' | 'apy' | 'deposited'
  const [sortDir, setSortDir] = useState('desc')
  const [onlyMine, setOnlyMine] = useState(false)
  const [onlyStable, setOnlyStable] = useState(false)

  const anyStablecoinVault = vaults.some((v) => v.tags?.includes('stablecoin'))

  const sortValue = useMemo(() => ({
    ...SORTABLE,
    // Sort by the COMBINED figure the column now shows (fees + emissions), not fees alone.
    // -Infinity, not 0: a vault with neither a fee nor an emission reading sorts below one sitting at
    // a real zero, matching how the column itself distinguishes "—" from "0.00".
    apy: (v) => apyInfoFor(v).combined ?? -Infinity,
    horizon: (v) => horizonPerDayFor(v.address) ?? -Infinity,
    volume: (v) => weeklyVolumeUsdFor(v.address) ?? -Infinity,
    fees: (v) => weeklyFeesUsdFor(v.address) ?? -Infinity,
    deposited: (v) => v.userTvlUsd || 0,
  }), [apyFor, horizonPerDayFor, weeklyVolumeUsdFor, weeklyFeesUsdFor, horizonUsd, hasHorizonPrice])

  // Protocol-wide weekly totals for the header cards. Sum only the vaults that actually have a
  // real figure - a null (no history / no DexScreener page) contributes nothing rather than a
  // zero that would drag the total down. `hasAny` distinguishes "genuinely $0" from "nothing
  // known yet", so the card shows "—" until at least one vault reports.
  const weeklyTotals = useMemo(() => {
    let volume = 0, fees = 0, pending = 0, hasVolume = false, hasFees = false, hasPending = false
    for (const v of vaults) {
      const vol = weeklyVolumeUsdFor(v.address)
      if (vol != null) { volume += vol; hasVolume = true }
      const fee = weeklyFeesUsdFor(v.address)
      if (fee != null) { fees += fee; hasFees = true }
      const p = pendingFeesUsdFor(v.address)
      if (p != null && p > 0) { pending += p; hasPending = true }
    }
    return { volume, fees, pending, hasVolume, hasFees, hasPending }
  }, [vaults, weeklyVolumeUsdFor, weeklyFeesUsdFor, pendingFeesUsdFor])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    let list = vaults.filter((v) => {
      if (v.loading) return false // no token metadata yet; a row for it would read token0 of undefined
      if (venue !== 'all' && v.venue !== venue) return false
      if (onlyMine && !v.hasPosition) return false
      if (onlyStable && !v.tags?.includes('stablecoin')) return false
      if (!q) return true
      return (
        (v.name || '').toLowerCase().includes(q) ||
        (v.token0?.symbol || '').toLowerCase().includes(q) ||
        (v.token1?.symbol || '').toLowerCase().includes(q) ||
        v.address.toLowerCase().includes(q)
      )
    })
    const val = sortValue[sortBy] || sortValue.tvl
    list = [...list].sort((a, b) => {
      const av = val(a), bv = val(b)
      const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv
      return sortDir === 'asc' ? cmp : -cmp
    })
    return list
  }, [vaults, venue, search, sortBy, sortDir, onlyMine, onlyStable, sortValue])

  /// USDx-quoted vaults, split out of the main table below rather than mixed into it.
  ///
  /// These are a genuinely different proposition from the rest: a pair quoted in USDx has one leg
  /// that holds its value, so impermanent loss and the USD value of the position behave nothing
  /// like they do in a two-volatile pair. Someone deciding where to put stable-denominated capital
  /// is asking a different question from someone farming a volatile pair, and a single sorted table
  /// makes them compare rows that aren't comparable.
  ///
  /// Matched on token ADDRESS rather than the symbol string - a vault holding some other token that
  /// happens to call itself "USDx" or "HORIZON" must not be quietly filed under Horizon's own.
  ///
  /// Three named markets, in precedence order (a vault falls into the FIRST that matches):
  ///   Horizon Vaults    - either leg is HORIZON (HORIZON/WETH, HORIZON/TITANX, HORIZON/USDx, ...).
  ///   USDx Markets     - either leg is USDx and it is not a HORIZON pair (WETH/USDx, TITANX/USDx, ...).
  ///   TitanX Ecosystem - either leg is a TitanX-ecosystem token (TITANX / DRAGONX / ORX / AURYN /
  ///                      X28), covering both TITANX/WETH and the intra-ecosystem pairs.
  ///   Other            - anything else (e.g. hUSD/USDC).
  /// HORIZON wins over USDx (HORIZON/USDx is a Horizon vault), and USDx wins over TitanX (TITANX/USDx is
  /// a USDx-market vault), because the stronger leg is the better signal about what the position is.
  const [horizonVaults, usdxVaults, titanVaults, otherVaults] = useMemo(() => {
    const horizon = (ADDRESSES.HORIZON_TOKEN || '').toLowerCase()
    const usdx = (ADDRESSES.USDX || '').toLowerCase()
    const titanEco = new Set([
      '0xf19308f923582a6f7c465e5ce7a9dc1bec6665b1', // TITANX
      '0x96a5399d07896f757bd4c6ef56461f58db951862', // DRAGONX
      '0xd536e7a9543cf9867a580b45cec7f748a1fe11ec', // ORX
      '0x58d7fa7356d68c494c2de09f95d55c2ff2d38bd3', // AURYN
      '0x5c47902c8c80779cb99235e42c354e53f38c3b0d', // X28
    ])
    const t0 = (v) => v.token0?.address?.toLowerCase()
    const t1 = (v) => v.token1?.address?.toLowerCase()
    const isHorizon = (v) => horizon && (t0(v) === horizon || t1(v) === horizon)
    const isUsdx = (v) => usdx && (t0(v) === usdx || t1(v) === usdx)
    const isTitan = (v) => titanEco.has(t0(v)) || titanEco.has(t1(v))
    const se = [], us = [], ti = [], ot = []
    for (const v of filtered) {
      if (isHorizon(v)) se.push(v)
      else if (isUsdx(v)) us.push(v)
      else if (isTitan(v)) ti.push(v)
      else ot.push(v)
    }
    return [se, us, ti, ot]
  }, [filtered])

  function toggleSort(key) {
    if (sortBy === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortBy(key); setSortDir('desc') }
  }

  return (
    <div id="vaults">
      <SectionHeader
        title="Vaults"
        subtitle="Put your capital to work. Smart vaults continuously rebalance concentrated positions and automatically compound fees across Uniswap V3 & V4."
      />

      {/* Both bar and columns stay hidden until the NAV indexer has real trailing figures - at
          launch every one reads "—", and two empty hero cards above the fold is worse than the
          space they'd hold for. They reappear on their own once one vault reports. */}
      {(weeklyTotals.hasVolume || weeklyTotals.hasFees || weeklyTotals.hasPending) && <WeeklyStatsBar totals={weeklyTotals} />}

      {/* Spotlight is a shortcut into a long list. With four vaults or fewer it just re-renders the
          whole table above it, so it only earns its space past that. */}
      {vaults.length > 4 && <Spotlight vaults={vaults} apyInfoFor={apyInfoFor} />}

      <div style={{ marginTop: 24, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {VENUE_FILTERS.map((f) => (
            <FilterPill key={f.id} active={venue === f.id} onClick={() => setVenue(f.id)}>{f.label}</FilterPill>
          ))}
          {user && (
            <FilterPill active={onlyMine} onClick={() => setOnlyMine((v) => !v)}>My Positions</FilterPill>
          )}
          {anyStablecoinVault && (
            <FilterPill active={onlyStable} onClick={() => setOnlyStable((v) => !v)}>{TAG_LABELS.stablecoin}</FilterPill>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <Input value={search} onChange={setSearch} placeholder="Search by asset or vault address" />
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        {notDeployed ? (
          <EmptyState title="Vault system not deployed on this network."
            body="Point your wallet at a network where Horizon's vault system is live." />
        ) : isLoading ? (
          <LoadingRows />
        ) : filtered.length === 0 ? (
          vaults.length === 0 ? (
            <EmptyState
              title="No vaults yet."
              body="Horizon's vault system is deployed and ready. The first vault appears here as soon as genesis creates one." />
          ) : (
            <EmptyState title="No vaults match your filters." body="Try clearing the search or switching venue." />
          )
        ) : (
          // Both groups share one sort/filter state deliberately - they're two views of the same
          // list, not two independent tables, so sorting by APY should reorder both at once.
          // Headings appear only when there is something to contrast WITH - a lone heading over
          // the only table on the page is noise.
          (() => {
            const groups = [
              ['Horizon Vaults', horizonVaults],
              ['USDx Markets', usdxVaults],
              ['TitanX Ecosystem', titanVaults],
              ['Other', otherVaults],
            ].filter(([, list]) => list.length > 0)
            return groups.map(([title, list]) => (
              <div key={title}>
                {groups.length > 1 && <MarketHeading title={title} />}
                <VaultTable vaults={list} apyInfoFor={apyInfoFor} horizonPerDayFor={horizonPerDayFor}
                  weeklyVolumeUsdFor={weeklyVolumeUsdFor} weeklyFeesUsdFor={weeklyFeesUsdFor}
                  showVolume={weeklyTotals.hasVolume} showFees={weeklyTotals.hasFees}
                  sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
              </div>
            ))
          })()
        )}
      </div>

      {/* Same page, separate section. A ALM vault earns trading fees on a two-token position and
          carries divergence risk; a staking vault earns emissions on a balance that never changes
          size. Listing them together would flatten exactly the distinction someone choosing
          between them needs. */}
      <StakingVaultsPanel />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// Protocol-wide weekly figures at the top of the page: total trading volume the vaults did this
// week, and total fees they earned their depositors. Boxed hero cells, the same chrome the market
// stat rows use. Both read "—" until the NAV indexer has a week of history and (for volume) a
// DexScreener-covered chain - real data only, never a fabricated headline number.
// ─────────────────────────────────────────────────────────────────

function WeeklyStatsBar({ totals }) {
  return (
    <div style={{ marginTop: 24, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
      <Stat boxed label="Weekly volume"
        value={totals.hasVolume ? fmtUsd(totals.volume) : '—'}
        sub="Trading volume across all vaults, last 7 days" />
      {/* Trailing realised fees (NAV growth) once there is a week of history; until then, the LIVE
          uncollected fees the positions have earned (read off the pool's fee-growth accumulators) so
          the figure reflects real trading immediately instead of sitting blank. */}
      <Stat boxed label={totals.hasFees ? 'Weekly fees' : 'Fees accruing'}
        value={totals.hasFees ? fmtUsd(totals.fees) : totals.hasPending ? fmtUsd(totals.pending) : '—'}
        sub={totals.hasFees ? 'Fees earned for depositors, last 7 days' : 'Earned by vault positions, not yet compounded'} />
    </div>
  )
}

function MarketHeading({ title, subtitle }) {
  return (
    <div style={{ marginTop: 28, marginBottom: 12 }}>
      <div style={{ fontFamily: T.fontD, fontSize: T.fs.h3, color: T.text, letterSpacing: '-0.01em' }}>{title}</div>
      {/* Conditional so a heading with no subtitle leaves no empty block behind it. Both headings
          are title-only now, and the prop is kept because the component is the general case. */}
      {subtitle && (
        <div style={{ fontFamily: T.fontM, fontSize: T.fs.caption, color: T.textMuted, marginTop: 4, maxWidth: 620, lineHeight: 1.55 }}>
          {subtitle}
        </div>
      )}
    </div>
  )
}


// ─────────────────────────────────────────────────────────────────
// Spotlight - three vaults by TVL plus one dedicated "New" slot for the latest-listed vault, "worth
// a closer look." Still real data only: the New slot is the genuinely most-recently-listed vault
// (VaultFactory registration order, last entry), NOT a fabricated hype badge - it exists because a
// pure TVL ranking is rich-get-richer, so a freshly listed vault (near-zero TVL) would never surface
// exactly when it most needs discovery. Deduped so the newest vault never appears twice.
// ─────────────────────────────────────────────────────────────────

function Spotlight({ vaults, apyInfoFor }) {
  const { picks, newestAddr } = useMemo(() => {
    // Only fully-loaded vaults have token metadata to render; a still-loading one has no token0.
    const ready = vaults.filter((v) => !v.loading)
    // Registration order: activeVaults() only ever appends, so the last entry is the latest listing.
    const newest = ready.length ? ready[ready.length - 1] : null
    const byTvl = ready
      .filter((v) => v.tvlUsd > 0 && v.address !== newest?.address)
      .sort((a, b) => b.tvlUsd - a.tvlUsd)
      .slice(0, 3)
    return { picks: newest ? [...byTvl, newest] : byTvl, newestAddr: newest?.address ?? null }
  }, [vaults])
  if (picks.length === 0) return null
  return (
    <Panel style={{ padding: 24, marginTop: 24 }}>
      <SectionLabel>Spotlight</SectionLabel>
      <div style={{ marginTop: 4, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>
        The vaults carrying the most capital, plus the newest listing.
      </div>
      <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12 }}>
        {picks.map((v) => <SpotlightCard key={v.address} vault={v} info={apyInfoFor(v)} isNew={v.address === newestAddr} />)}
      </div>
    </Panel>
  )
}

function SpotlightCard({ vault, info, isNew }) {
  const [sym0, sym1] = orderedPairSymbols(vault.token0.address, vault.token1.address, vault.token0.symbol, vault.token1.symbol)
  const pairName = `${sym0}-${sym1}`
  return (
    <Link to={`/vaults/${vault.address}`} style={{ textDecoration: 'none' }}>
      <div style={spotlightCard}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <PairGlyph symbols={[sym0, sym1]} size={40} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ color: T.text, fontFamily: T.fontD, fontWeight: 700, fontSize: T.fs.body }}>{pairName}</span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <Badge color={vault.venue === 'v4' ? T.purple : T.cyan}>{vault.venue === 'v4' ? 'Uniswap V4' : 'Uniswap V3'}</Badge>
              {isNew && <Badge color={T.positive}>New</Badge>}
            </div>
          </div>
        </div>
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <div style={miniLabel}>APY</div>
            <div style={miniValue}><ApyValue info={info} /></div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={miniLabel}>TVL</div>
            <div className="mono" style={miniValue}>{fmtUsd(vault.tvlUsd)}</div>
          </div>
        </div>
      </div>
    </Link>
  )
}

// The APY column/stat: a single COMBINED figure (fees + emissions) with the breakdown on hover.
// Shared by the table rows and the Spotlight cards so both read identically. `—` when neither leg is
// knowable; a dotted underline signals the hover affordance. `style` lets a caller set the font size
// (the table sets T.fs.body; the Spotlight card inherits its own miniValue size).
function ApyValue({ info, style = {} }) {
  const { combined, feePct, emissionPct, hasFee, hasEmission } = info
  if (combined == null) {
    return <span style={{ color: T.textDim, fontFamily: T.fontN, ...style }}>—</span>
  }
  const content = (
    <div style={{ minWidth: 176, textTransform: 'none', letterSpacing: 0, whiteSpace: 'nowrap' }}>
      <div style={{ color: T.text, fontFamily: T.fontM, fontSize: T.fs.caption, fontWeight: 700, marginBottom: 8 }}>
        Combined APY
      </div>
      <BreakdownRow dot={T.positive} label="Trading fees"
        value={hasFee ? fmtPct(feePct) : '—'} note={hasFee ? '7-day' : 'no history yet'} />
      <BreakdownRow dot={T.accent} label="HORIZON emissions"
        value={hasEmission ? fmtPct(emissionPct) : '—'} note={hasEmission ? 'estimated' : 'not incentivized'} />
    </div>
  )
  return (
    <Tooltip content={content}>
      <span className="mono" style={{
        color: combined > 0 ? T.positive : T.textDim, fontFamily: T.fontN, fontWeight: 700,
        borderBottom: `1px dotted ${T.textDim}`, cursor: 'help', ...style,
      }}>
        {fmtPct(combined)}
      </span>
    </Tooltip>
  )
}

function BreakdownRow({ dot, label, value, note }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, padding: '3px 0' }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 7, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption }}>
        <i style={{ width: 7, height: 7, borderRadius: '50%', background: dot, display: 'inline-block', flexShrink: 0 }} />
        {label}
      </span>
      <span className="mono" style={{ color: T.text, fontFamily: T.fontN, fontSize: T.fs.caption, fontWeight: 700 }}>
        {value}
        {note && <span style={{ color: T.textDim, fontWeight: 400, marginLeft: 6 }}>{note}</span>}
      </span>
    </div>
  )
}

function VaultTable({ vaults, apyInfoFor, horizonPerDayFor, weeklyVolumeUsdFor, weeklyFeesUsdFor, showVolume, showFees, sortBy, sortDir, onSort }) {
  // Narrow the table by exactly the width of whichever weekly columns are deferred, so hiding them
  // tightens the layout instead of leaving a gap the flex row would stretch to fill.
  const minWidth = 1040 - (showVolume ? 0 : 120) - (showFees ? 0 : 110)
  return (
    <Panel style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth }}>
          <div style={headerRow}>
            <span style={{ ...headCell, flex: '1 1 auto', cursor: 'pointer' }} {...clickable(() => onSort('name'))}>
              Vault{sortArrow('name', sortBy, sortDir)}
            </span>
            <span style={{ ...headCell, width: 90, textAlign: 'right', cursor: 'pointer' }} {...clickable(() => onSort('apy'))}>
              APY{sortArrow('apy', sortBy, sortDir)}
            </span>
            <span style={{ ...headCell, width: 110, textAlign: 'right', cursor: 'pointer' }} {...clickable(() => onSort('horizon'))}>
              HORIZON / day{sortArrow('horizon', sortBy, sortDir)}
            </span>
            {showVolume && (
              <span style={{ ...headCell, width: 120, textAlign: 'right', cursor: 'pointer' }} {...clickable(() => onSort('volume'))}>
                Weekly volume{sortArrow('volume', sortBy, sortDir)}
              </span>
            )}
            {showFees && (
              <span style={{ ...headCell, width: 110, textAlign: 'right', cursor: 'pointer' }} {...clickable(() => onSort('fees'))}>
                Weekly fees{sortArrow('fees', sortBy, sortDir)}
              </span>
            )}
            <span style={{ ...headCell, width: 140, textAlign: 'right', cursor: 'pointer' }} {...clickable(() => onSort('tvl'))}>
              TVL{sortArrow('tvl', sortBy, sortDir)}
            </span>
            <span style={{ ...headCell, width: 130, textAlign: 'right', cursor: 'pointer' }} {...clickable(() => onSort('deposited'))}>
              Deposited{sortArrow('deposited', sortBy, sortDir)}
            </span>
          </div>
          {vaults.map((v) => (
            <VaultRow key={v.address} vault={v} info={apyInfoFor(v)} horizonPerDay={horizonPerDayFor(v.address)}
              weeklyVolume={weeklyVolumeUsdFor(v.address)} weeklyFees={weeklyFeesUsdFor(v.address)}
              showVolume={showVolume} showFees={showFees} />
          ))}
        </div>
      </div>
    </Panel>
  )
}

function sortArrow(key, sortBy, sortDir) {
  if (sortBy !== key) return null
  return <span style={{ marginLeft: 4, opacity: 0.7 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>
}

function VaultRow({ vault, info, horizonPerDay, weeklyVolume, weeklyFees, showVolume, showFees }) {
  const [sym0, sym1] = orderedPairSymbols(vault.token0.address, vault.token1.address, vault.token0.symbol, vault.token1.symbol)
  const pairName = `${sym0}-${sym1}`
  return (
    <Link
      to={`/vaults/${vault.address}`}
      style={{ ...row, textDecoration: 'none', cursor: 'pointer', transition: 'background 0.15s ease' }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)' }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
    >
      <div style={{ flex: '1 1 auto', display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <PairGlyph symbols={[sym0, sym1]} size={40} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: T.text, fontFamily: T.fontD, fontSize: T.fs.ui, fontWeight: 700 }}>{pairName}</span>
            {vault.paused && <Badge color={T.warning}>Paused</Badge>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <Badge color={vault.venue === 'v4' ? T.purple : T.cyan}>{vault.venue === 'v4' ? 'Uniswap V4' : 'Uniswap V3'}</Badge>
            <Badge color={T.accent}>ALM Vault</Badge>
            {vault.tags?.includes('stablecoin') && <Badge color={T.positive}>{TAG_LABELS.stablecoin}</Badge>}
          </div>
        </div>
      </div>
      {/* Combined headline (realized trailing fee APY + estimated HORIZON emission APR), with the
          split disclosed on hover. "—" stays the honest reading when neither leg is knowable. */}
      <div style={{ width: 90, textAlign: 'right' }}>
        <ApyValue info={info} style={{ fontSize: T.fs.body }} />
      </div>
      {/* Rate only - claiming lives on Portfolio, which is this app's single claim surface (see
          useVaultEmissions.js). A vault registered at zero weight shows a real 0; one not
          registered for emissions at all shows "—". */}
      <div style={{ width: 110, textAlign: 'right' }}>
        <span className="mono" style={{ color: horizonPerDay > 0 ? T.accent : T.textDim, fontFamily: T.fontN, fontSize: T.fs.body, fontWeight: horizonPerDay > 0 ? 700 : 400 }}>
          {horizonPerDay != null ? fmt(horizonPerDay, 2) : '—'}
        </span>
      </div>
      {/* Weekly trading volume and weekly fees earned, both real trailing-7-day figures from the
          NAV indexer - deferred entirely (header + cell) until at least one vault reports, rather
          than a column of dashes; the same not-yet-knowable convention the APY column uses, taken
          one step further because these two are all-or-nothing across the whole table. */}
      {showVolume && (
        <div style={{ width: 120, textAlign: 'right' }}>
          <span className="mono" style={{ color: weeklyVolume != null ? T.text : T.textDim, fontFamily: T.fontN, fontSize: T.fs.body, fontWeight: weeklyVolume != null ? 700 : 400 }}>
            {weeklyVolume != null ? fmtUsd(weeklyVolume) : '—'}
          </span>
        </div>
      )}
      {showFees && (
        <div style={{ width: 110, textAlign: 'right' }}>
          <span className="mono" style={{ color: weeklyFees != null && weeklyFees > 0 ? T.positive : T.textDim, fontFamily: T.fontN, fontSize: T.fs.body, fontWeight: weeklyFees != null ? 700 : 400 }}>
            {weeklyFees != null ? fmtUsd(weeklyFees) : '—'}
          </span>
        </div>
      )}
      <div style={{ width: 140, textAlign: 'right' }}>
        <span className="mono" style={{ color: T.text, fontFamily: T.fontN, fontSize: T.fs.body, fontWeight: 700 }}>
          {vault.tvlUsd > 0 ? `${vault.fullyPriced ? '' : '≥'}${fmtUsd(vault.tvlUsd)}` : `${fmt(vault.token0.amount, 2)} + ${fmt(vault.token1.amount, 2)}`}
        </span>
      </div>
      <div style={{ width: 130, textAlign: 'right' }}>
        <span className="mono" style={{ color: vault.hasPosition ? T.positive : T.textDim, fontFamily: T.fontN, fontSize: T.fs.body, fontWeight: 700 }}>
          {vault.hasPosition ? `${vault.fullyPriced ? '' : '≥'}${fmtUsd(vault.userTvlUsd)}` : '—'}
        </span>
      </div>
    </Link>
  )
}

function LoadingRows() {
  return (
    <Panel style={{ padding: 20 }}>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ ...row, borderBottom: i === 2 ? 'none' : row.borderBottom }}>
          <Skeleton width={140} height={16} />
          <Skeleton width={80} height={16} />
          <Skeleton width={40} height={16} />
          <Skeleton width={70} height={16} />
          <Skeleton width={70} height={16} />
          <Skeleton width={70} height={16} />
          <Skeleton width={70} height={16} />
        </div>
      ))}
    </Panel>
  )
}

function EmptyState({ title, body }) {
  return (
    <Panel elevated style={{ padding: 40, textAlign: 'center' }}>
      <div style={{ color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.ui, fontWeight: 700 }}>{title}</div>
      <div style={{ marginTop: 8, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.6 }}>{body}</div>
    </Panel>
  )
}

// ─────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────



const spotlightCard = {
  padding: 18, borderRadius: T.r.lg, border: `1px solid ${T.border}`,
  background: 'rgba(255,255,255,0.02)', transition: 'border-color 0.15s ease, background 0.15s ease',
}
const miniLabel = { color: T.textDim, fontFamily: T.fontM, fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }
const miniValue = { color: T.text, fontFamily: T.fontN, fontSize: T.fs.body, fontWeight: 700, marginTop: 3 }

// headerRow / headCell / row now live in tableChrome.js - see that file for why they moved.
